/**
 * Blocks & proxies: what fetching knows about bot protection right now.
 *
 * Proxies (and "direct"): how they did lately, which sites they are resting from,
 * which sites blocked them. Tools (DataDome, Cloudflare...): how many sites use each,
 * and the careful pages open in the browsers. Sites: each one's tools, blocks, and
 * whether crawl4ai or plain HTTP is skipped there. Backend: jcseo/fetching/state.py.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import type { ProtectionReport, ProtectionSite } from '../types';

const REFRESH_MS = 15_000;

const ADAPTER_NAMES: Record<string, string> = {
  plain_http: 'Plain HTTP',
  crawl4ai: 'crawl4ai',
  plugin_v2: 'Connected extension',
  plugin_v1: 'Queued extension',
  serper: 'Serper',
};

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>}
      </div>
      <div className="p-4 overflow-x-auto">{children}</div>
    </section>
  );
}

function Chip({ children, tone = 'gray' }: { children: React.ReactNode; tone?: 'gray' | 'amber' | 'red' | 'green' }) {
  const cls = {
    gray: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
    amber: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
    red: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    green: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  }[tone];
  return <span className={`inline-block px-1.5 py-0.5 mr-1 mb-1 text-[11px] rounded ${cls}`}>{children}</span>;
}

const th = 'text-left text-xs font-medium text-gray-500 dark:text-gray-400 pb-2 pr-4 whitespace-nowrap';
const td = 'py-2 pr-4 align-top text-xs text-gray-700 dark:text-gray-200';

function minutes(s: number): string {
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} min`;
}

function ratio(blocked: number, tries: number) {
  if (!tries) return <span className="text-gray-400">none</span>;
  const bad = blocked / tries > 0.2;
  return (
    <span className={bad ? 'text-red-600 dark:text-red-400' : ''}>
      {blocked} blocked of {tries}
    </span>
  );
}

export default function NetworkProtection() {
  const [data, setData] = useState<ProtectionReport | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      setData(await api.getProtectionReport());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the report');
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const sites = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (data?.sites ?? []).filter(
      (s) => !f || s.site.includes(f) || s.tools.some((t) => t.toLowerCase().includes(f)),
    );
  }, [data, filter]);

  const traceSite = (site: ProtectionSite) => {
    const params = new URLSearchParams();
    params.set('tab', 'network');
    params.set('sub', 'trace');
    params.set('url', `https://${site.site}/`);
    setSearchParams(params);
  };

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="network-protection">
      {data.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
          <ul className="list-disc pl-5 space-y-1 text-sm text-amber-900 dark:text-amber-200">
            {data.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        A site's block counts for {data.settings.block_memory_days} days there. Sites that use the same
        bot protection avoid the blocked proxy for {data.settings.shared_block_hours} hours (set on
        Settings). Refreshes every {REFRESH_MS / 1000} s.{' '}
        <button type="button" onClick={load} className="text-blue-600 dark:text-blue-400 hover:underline">
          Refresh now
        </button>
      </p>

      <Section title="Proxies" hint="Each proxy, and fetches that go out direct from this server.">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className={th}>Proxy</th>
              <th className={th}>Last day</th>
              <th className={th}>Last week</th>
              <th className={th}>Resting from</th>
              <th className={th}>Blocked by (last 30 days)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.proxies.map((p) => (
              <tr key={p.egress} className={p.enabled ? '' : 'opacity-50'}>
                <td className={td}>
                  <div className="font-medium">{p.label}</div>
                  <div className="text-gray-400">
                    {p.list ? `${p.list} · ` : ''}
                    {p.uses.length ? p.uses.join(', ') : 'not used'}
                    {!p.enabled && ' · off'}
                  </div>
                </td>
                <td className={td}>{ratio(p.day_blocked, p.day_tries)}</td>
                <td className={td}>{ratio(p.week_blocked, p.week_tries)}</td>
                <td className={td}>
                  {p.resting.length === 0 && <span className="text-gray-400">none</span>}
                  {p.resting.map((r) => (
                    <Chip key={r.host} tone="amber">
                      {r.host} · {minutes(r.seconds)}
                    </Chip>
                  ))}
                </td>
                <td className={td}>
                  {p.blocked_on.length === 0 && <span className="text-gray-400">none</span>}
                  {p.blocked_on.map((b) => (
                    <Chip key={`${b.site}-${b.adapter}`} tone="red">
                      {b.site} · {ADAPTER_NAMES[b.adapter] ?? b.adapter} · {b.ago}
                    </Chip>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section
        title="Bot protection tools"
        hint="Strict tools make every page on their sites careful: one browser tab at a time per tool, 20-60 s apart."
      >
        <table className="min-w-full">
          <thead>
            <tr>
              <th className={th}>Tool</th>
              <th className={th}>Sites seen</th>
              <th className={th}>Sites with open blocks</th>
              <th className={th}>Proxies avoided on its sites</th>
              <th className={th}>Careful pages now</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {data.tools.map((t) => (
              <tr key={t.tool}>
                <td className={td}>
                  <span className="font-medium">{t.tool}</span>
                  {t.strict && <Chip tone="amber">strict</Chip>}
                </td>
                <td className={td}>{t.sites}</td>
                <td className={td}>{t.sites_with_blocks}</td>
                <td className={td}>
                  {t.proxies_passed_over.length === 0 ? (
                    <span className="text-gray-400">none</span>
                  ) : (
                    t.proxies_passed_over.map((e) => <Chip key={e}>{e}</Chip>)
                  )}
                </td>
                <td className={td}>
                  {t.careful_now.length === 0 && <span className="text-gray-400">none</span>}
                  {t.careful_now.map((c) => (
                    <div key={c.group}>
                      {c.group === 'house' ? 'shared browsers' : `browsers of user ${c.group.replace('own:', '')}`}:{' '}
                      {c.open ? `${c.open} open` : ''}
                      {c.open && c.next_in_s ? ', ' : ''}
                      {c.next_in_s ? `next in ${c.next_in_s} s` : ''}
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section
        title={`Sites (${data.sites.length})`}
        hint="Sites that showed a bot protection tool or blocked a fetch, newest first."
      >
        <input
          type="search"
          placeholder="Filter by site or tool"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-3 w-full sm:w-72 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        />
        <table className="min-w-full">
          <thead>
            <tr>
              <th className={th}>Site</th>
              <th className={th}>Tools</th>
              <th className={th}>Now</th>
              <th className={th}>Blocks</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sites.map((s) => (
              <tr key={s.site}>
                <td className={td}>
                  <div className="font-medium">{s.site}</div>
                  <div className="text-gray-400">updated {s.updated_ago}</div>
                </td>
                <td className={td}>
                  {s.tools.length === 0 && <span className="text-gray-400">none seen</span>}
                  {s.tools.map((t) => (
                    <Chip key={t}>{t}</Chip>
                  ))}
                </td>
                <td className={td}>
                  {Object.entries(s.skipped).map(([a, why]) => (
                    <div key={a} className="text-red-600 dark:text-red-400">
                      {ADAPTER_NAMES[a] ?? a} skipped: {why}
                    </div>
                  ))}
                  {s.careful.length > 0 && <div>Careful pages ({s.careful.join(', ')})</div>}
                  {Object.keys(s.skipped).length === 0 && s.careful.length === 0 && (
                    <span className="text-gray-400">fetched as usual</span>
                  )}
                </td>
                <td className={td}>
                  {s.blocks.length === 0 && <span className="text-gray-400">none</span>}
                  {s.blocks.map((b) => (
                    <div key={b.key} className={b.counts ? '' : 'text-gray-400'}>
                      {ADAPTER_NAMES[b.adapter] ?? b.adapter}
                      {b.egress ? ` via ${b.egress}` : ''}: blocked {b.blocked_ago}
                      {b.ok_ago ? `, last got the page ${b.ok_ago}` : ''}
                      {!b.counts && ' (no longer counts)'}
                    </div>
                  ))}
                </td>
                <td className={td}>
                  <button
                    type="button"
                    onClick={() => traceSite(s)}
                    className="text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                  >
                    Trace
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sites.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No sites yet.</p>}
      </Section>
    </div>
  );
}
